import { StyledButton, StyledFieldSet, StyledForm, StyledFormField } from '@/components/styles';
import { Checkbox, FormField, SelectDirections, SelectSpecialists, AttributionHiddenFields } from '@/components';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, FormContext } from 'react-hook-form';
import { regExp, legalLinks, normalizePhone, normalizeEmail } from '@/lib/config';
import { phoneValidateRules } from '@/lib/validatePhone';
import { usePromise } from '@/hooks';
import { useModal } from './Modal';
import axios from 'axios';
import api from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as gtag from '@/lib/gtag'
import { getFormAttributionPayload, getSessionCode, getPagePath, createSubmissionId } from '@/lib/attribution'
import { getFormSubmitErrorMessage } from '@/lib/formSubmitError'
import {
  compatibleDirectionCandidates,
  findCompatibleDirection,
  findDirection,
  pickCompatibleSpecialist,
  readSpecialists,
  resolveFormDirection,
} from '@/lib/formDirection'
import { useFormPage } from '@/contexts/FormPageContext'

export default function AppointmentForm() {
  const formMethods = useForm();
  const { register, handleSubmit, setValue, reset } = formMethods;
  const { t } = useTranslation('global');
  const router = useRouter();
  const {
    formDirection,
    formDoctor,
    serviceDirections = [],
    pageSpecialists = [],
    fillFromSpecialists = false,
  } = useFormPage();
  const directionTouched = useRef(false);
  const specialistTouched = useRef(false);
  const directionCheckKey = useRef('');

  const [activeDirection, setActiveDirection] = useState({});
  const [activeSpecialistId, setActiveSpecialistId] = useState(null);
  const [submissionId] = useState(createSubmissionId);

  const cancelTokenSource = useRef(axios.CancelToken.source());
  const appointmentPromise = usePromise(
    () => api.appointment({ direction: activeDirection.slug, cancelToken: cancelTokenSource.token }),
    [activeDirection],
    {},
    () => cancelTokenSource.current.cancel()
  );

  const { specialists: bookingSpecialists = [], directions = [] } = appointmentPromise.result || {};
  const [directionSpecialists, setDirectionSpecialists] = useState({ slug: '', list: [], status: 'idle' });

  useEffect(() => {
    const slug = activeDirection.slug
    if (!slug) {
      setDirectionSpecialists({ slug: '', list: [], status: 'resolved' })
      return
    }

    let cancelled = false
    const source = axios.CancelToken.source()
    setDirectionSpecialists({ slug, list: [], status: 'pending' })
    api.specialistsByDirection({ slug, cancelToken: source.token })
      .then((body) => {
        if (cancelled) return
        setDirectionSpecialists({ slug, list: readSpecialists(body), status: 'resolved' })
      })
      .catch((error) => {
        if (cancelled || axios.isCancel(error)) return
        setDirectionSpecialists({ slug, list: [], status: 'rejected' })
      })

    return () => {
      cancelled = true
      source.cancel()
    }
  }, [activeDirection.slug]);

  const specialists = activeDirection.slug ? directionSpecialists.list : bookingSpecialists
  const specialistsStatus = activeDirection.slug ? directionSpecialists.status : appointmentPromise.status
  const specialistsReady =
    specialistsStatus === 'resolved' &&
    (!activeDirection.slug || directionSpecialists.slug === activeDirection.slug)

  const [status, setStatus] = useState({
    success: false,
    loading: false,
    error: '',
  });
  const errorInput = useRef();

  useEffect(() => {
    register({ name: 'direction' });
    setValue('direction', '');
    register({ name: 'specialist' });
    setValue('specialist', '');
    register({ name: 'doctor' });
    setValue('doctor', '');
  }, []);

  useEffect(() => {
    if (directionTouched.current || !directions.length) return

    const pathSlug = resolveFormDirection(router.asPath, directions, '')
    const candidates = compatibleDirectionCandidates(formDirection, serviceDirections, pathSlug)
    const checkKey = `${formDoctor}|${candidates.join('\0')}`
    if (directionCheckKey.current === checkKey) return

    let cancelled = false

    const applySlug = (slug) => {
      if (cancelled || directionTouched.current) return
      directionCheckKey.current = checkKey
      if (!slug || slug === activeDirection.slug) return
      const direction = findDirection(directions, slug)
      if (!direction) return
      setActiveDirection(direction)
      setValue('direction', direction.slug || '')
    }

    if (!formDoctor) {
      applySlug(resolveFormDirection(router.asPath, directions, formDirection))
      return () => {
        cancelled = true
      }
    }

    findCompatibleDirection(directions, candidates, formDoctor, async (slug) => {
      return readSpecialists(await api.specialistsByDirection({ slug }))
    }).then((slug) => {
      applySlug(slug)
    })

    return () => {
      cancelled = true
    }
  }, [directions, router.asPath, formDirection, formDoctor, serviceDirections]);

  useEffect(() => {
    if (!specialistsReady) return
    if (!activeSpecialistId) return
    const stillThere = specialists.some((spec) => String(spec.id) === String(activeSpecialistId))
    if (stillThere) return
    setActiveSpecialistId(null)
    setValue('specialist', '')
    setValue('doctor', '')
  }, [specialists, specialistsReady, activeSpecialistId]);

  useEffect(() => {
    if (specialistTouched.current || !specialistsReady || !activeDirection.slug) return
    const specialist = pickCompatibleSpecialist({
      formDoctor,
      pageSpecialists,
      directionSpecialists: specialists,
      fillFromSpecialists,
    })
    if (!specialist || String(activeSpecialistId) === String(specialist.id)) return
    setActiveSpecialistId(specialist.id)
    setValue('specialist', specialist.id)
    setValue('doctor', specialist.id)
  }, [specialists, specialistsReady, formDoctor, pageSpecialists, fillFromSpecialists, activeDirection.slug]);

  useEffect(() => {
    if (status.error && errorInput.current) {
      errorInput.current.focus();
    }
  }, [status.error]);

  const finishWithError = (error) => {
    setStatus((prevStatus) => ({
      ...prevStatus,
      loading: false,
      error: getFormSubmitErrorMessage(error),
    }));
  };

  const onFormSubmit = async (data) => {
    const phone = normalizePhone(data.phone);
    const requestData = {
      ...data,
      phone,
      ...getFormAttributionPayload(),
      sid: getSessionCode(),
      form_id: 'book-modal',
      page_path: getPagePath(),
      submission_id: submissionId,
      direction: activeDirection.slug || '',
      doctor: activeSpecialistId || '',
      specialist: activeSpecialistId || '',
    };

    setStatus((prevStatus) => ({ ...prevStatus, loading: true, error: '' }));

    try {
      const resp = await api.appointmentCreate(requestData);
      if (resp.result === 'success') {
        try {
          const hashedData = await api.hash({
            data: {
              phone_number: phone,
              email: normalizeEmail(data.email),
            },
          });
          gtag.gtag_report_conversion('AW-18230806173/_AKoCOzs2b0cEJ2NkPVD', { ...hashedData });
        } catch (error) {
          console.log(error);
          gtag.gtag_report_conversion('AW-18230806173/_AKoCOzs2b0cEJ2NkPVD');
        }

        reset();
        setStatus((prevStatus) => ({ ...prevStatus, loading: false, success: true }));
      } else {
        finishWithError();
      }
    } catch (error) {
      finishWithError(error);
    }
  };

  const handleDirectionChange = useCallback(
    (value) => {
      directionTouched.current = true
      const direction = findDirection(directions, value) || {}
      setActiveDirection(direction)
      setValue('direction', direction.slug || '')
    },
    [directions]
  );

  const handleSpecialistChange = useCallback(
    (value) => {
      specialistTouched.current = true
      const specialist = specialists.find((spec) => String(spec.id) === String(value))
      const id = specialist ? specialist.id : ''
      setActiveSpecialistId(id || null)
      setValue('specialist', id)
      setValue('doctor', id)
    },
    [specialists]
  );

  return status.success ? (
    <Success />
  ) : (
    <FormContext {...formMethods}>
      <StyledForm as="form" action="/" onSubmit={handleSubmit(onFormSubmit)} modal autoComplete="off">
        <AttributionHiddenFields />
        <StyledForm.Title>{t('appointmentForm.title')}</StyledForm.Title>
        <StyledForm.Desc>{t('appointmentForm.desc')}</StyledForm.Desc>

        {status.error && (
          <StyledForm.Error ref={errorInput} tabIndex={-1}>
            {status.error}
          </StyledForm.Error>
        )}

        <StyledForm.Block>
          <StyledFieldSet>
            <StyledFieldSet.Legend>{t('appointmentForm.personalTitle')}</StyledFieldSet.Legend>
            <StyledForm.Row>
              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.name')}
                  name="first_name"
                  validateRules={{
                    required: true,
                    validate: (value) => {
                      if (value.length && value.trim().length) {
                        return true;
                      } else {
                        return false;
                      }
                    },
                  }}
                  autoFocus
                />
              </StyledForm.Item>

              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.lastName')}
                  name="last_name"
                  validateRules={{
                    required: true,
                    validate: (value) => {
                      if (value.length && value.trim().length) {
                        return true;
                      } else {
                        return false;
                      }
                    },
                  }}
                />
              </StyledForm.Item>
            </StyledForm.Row>

            <StyledForm.Row>
              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.phone')}
                  name="phone"
                  type="tel"
                  inputmode="tel"
                  autoComplete="tel"
                  placeholder="050 123 4567"
                  validateRules={phoneValidateRules}
                />
              </StyledForm.Item>

              <StyledForm.Item>
                <FormField
                  label="E-mail"
                  name="email"
                  type="text"
                  inputmode="email"
                  validateRules={{
                    validate: (value) => {
                      if (value.length < 1) {
                        return true;
                      } else {
                        return regExp.email.test(value);
                      }
                    },
                  }}
                />
              </StyledForm.Item>
            </StyledForm.Row>
          </StyledFieldSet>
        </StyledForm.Block>

        <StyledForm.Block styledLast>
          <StyledFieldSet>
            <StyledFieldSet.Legend>{t('appointmentForm.serviceTitle')}</StyledFieldSet.Legend>

            {(directions.length > 0 || specialists.length > 0) && (
              <StyledForm.Row>
                <StyledForm.Item>
                  <StyledFormField noDecor>
                    <StyledFormField.Title>{t('appointmentForm.direction')}</StyledFormField.Title>

                    <SelectDirections
                      includeAllItemsVariant
                      directions={directions}
                      active={activeDirection}
                      onChange={handleDirectionChange}
                      placeholder={t('appointmentForm.chooseDirection')}
                      selectType="form"
                      selected={!!activeDirection?.slug}
                    />
                  </StyledFormField>
                </StyledForm.Item>

                <StyledForm.Item>
                  <StyledFormField noDecor>
                    <StyledFormField.Title>{t('appointmentForm.specialist')}</StyledFormField.Title>

                    <SelectSpecialists
                      specialists={specialists}
                      activeId={(specialists.find((spec) => spec.id === activeSpecialistId) || {}).id}
                      onChange={handleSpecialistChange}
                      placeholder={t('appointmentForm.chooseSpecialist')}
                      selectType="form"
                      selected={!!activeSpecialistId}
                      disabled={specialists.length === 0}
                    />
                  </StyledFormField>
                </StyledForm.Item>
              </StyledForm.Row>
            )}

            <StyledForm.Row>
              <StyledForm.Item>
                <FormField inputAs="textarea" label={t('appointmentForm.comment')} name="comment" />
              </StyledForm.Item>
            </StyledForm.Row>

            <StyledForm.Row>
              <StyledForm.Item>
                <Checkbox name="emailing" defaultChecked>
                  {t('appointmentForm.emailing.text')}{' '}
                  <a href={legalLinks.newsletter} target="_blank" rel="noopener noreferrer">
                    {t('appointmentForm.emailing.link')}
                  </a>
                  .
                </Checkbox>
              </StyledForm.Item>
            </StyledForm.Row>
          </StyledFieldSet>
        </StyledForm.Block>

        <StyledForm.Submit styledWide>
          <StyledButton type="submit" disabled={status.loading} isLoading={status.loading}>
            {t('appointmentForm.submit')}
            <StyledButton.Loader />
          </StyledButton>
        </StyledForm.Submit>

        {/* <StyledForm.Footer>
          {t('appointmentForm.legal.text')}{' '}
          <a href={legalLinks.publicOffer} target="_blank" rel="noopener noreferrer">
            {t('appointmentForm.legal.link')}
          </a>
          {'.'}
        </StyledForm.Footer> */}
      </StyledForm>
    </FormContext>
  );
}

function Success() {
  const modal = useModal();
  const { closeModal } = modal;
  const { t } = useTranslation('global');

  return (
    <StyledForm as="div" modal>
      <StyledForm.Title centered>{t('appointmentForm.thanks.title')}</StyledForm.Title>
      <StyledForm.Desc centered>{t('appointmentForm.thanks.desc')}</StyledForm.Desc>
      <StyledForm.Submit>
        <StyledButton type="button" onClick={closeModal}>
          {t('appointmentForm.thanks.button')}
        </StyledButton>
      </StyledForm.Submit>
    </StyledForm>
  );
}
